import React, {useEffect} from "react";
import { Footer, Header } from "../Components";
import FooterTopBar from "../Components/FooterTopBar";

export const PrivacyPolicy = () => {
  document.title = "Privacy Policy - Famra Cure";
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-left mb-8 text-sky-800">
            Privacy Policy
          </h1>
          
          <p className="text-gray-700 leading-relaxed mb-8 text-center">
            At Famra Cure, we take your privacy seriously. We are committed to protecting the personal and health data you share with us across our platforms. It is our legal and ethical obligation to keep your information private and secure. We work continuously to ensure your data is handled with the utmost care and professional confidentiality.
          </p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Overview</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                This Privacy Policy explains how Famra Cure ("we," "us," or "Famra Cure") collects, uses, and protects your personal information. It applies to all Users, including Practitioners, End-Users, and Website visitors.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Your Agreement</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By using our Services or providing your information, you agree to the practices described in this policy. We reserve the right to update this policy at any time. If you do not agree with these terms, please discontinue use of the Services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Key Scope</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-3 mb-4">
                <li>
                  <strong>Third Parties:</strong> This policy only applies to Famra Cure. It does not cover the practices of business partners or other digital services you may access through our platform.
                </li>
                <li>
                  <strong>Representing Others:</strong> If you use Famra Cure for someone else (like a child or employer), you confirm you have the legal authority to accept this policy on their behalf.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To provide effective services, we collect personal information that identifies you when you register or use our platform. This data is provided voluntarily and includes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Contact Data:</strong> Your name, address, phone and email.</li>
                <li><strong>Demographic Data:</strong> Age, gender, date of birth, and postcode.</li>
                <li><strong>Identification & Preferences:</strong> CNIC number, passwords, and user preferences.</li>
                <li><strong>Medical Data:</strong> Healthcare providers visited, medical all sort of reports/ diagnosis, reasons for visits, dates, and any health history you choose to share.</li>
                <li><strong>Other Communications:</strong> Any information you include in emails or letters sent to us.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Traffic Data</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you interact with our Services, we automatically collect certain technical information ("Traffic Data"), including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Device & Access:</strong> Your IP address, domain server, device type, and web browser.</li>
                <li><strong>Source & Location:</strong> The referring webpage or source used to reach our Services and your geolocation data.</li>
                <li><strong>Usage Statistics:</strong> Other data regarding how your browser or device interacts with the Services.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on local laws, some Traffic Data may be classified as Personal Information. We may also collect additional data at the point of collection with your explicit consent.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">How We Collect Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We gather Personal Information and Traffic Data through your direct interactions with our Services and, occasionally, from third-party sources. This includes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Interactive Tools:</strong> When you search for providers, book appointments, or complete Medical History Forms.</li>
                <li><strong>Voluntary Input:</strong> Information shared via free-form text boxes, surveys, or questionnaires.</li>
                <li><strong>Software & Apps:</strong> Data transmitted from your device when you install our software (e.g., login status for alerts) or use our mobile app (e.g., location and device details).</li>
                <li><strong>Tracking Technologies:</strong> Data collected through cookies, web analytics, and other tracking tools.</li>
                <li><strong>Direct Contact:</strong> Information provided when using the "Contact Us" feature, sending emails, or reaching out to our support team.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Why We Need Your Data</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use your information to make Famra Cure work better for you. This includes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Service Delivery:</strong> Managing your bookings and answering your questions.</li>
                <li><strong>Reminders:</strong> Sending you text or email alerts for upcoming appointments.</li>
                <li><strong>Personalized Content:</strong> Showing you doctors and health resources that match your interests.</li>
                <li><strong>Improvements:</strong> Using statistics to understand how people use our app so we can add better features.</li>
                <li><strong>Security:</strong> Keeping your account safe from fraud or hacking.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">How We Share Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may disclose information collected from you in the following ways:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-3 mb-4">
                <li>
                  <strong>With Healthcare Providers:</strong> When you schedule an appointment, we share your relevant Personal Information and Medical History Forms with the selected Doctors.
                </li>
                <li>
                  <strong>With Service Vendors:</strong> We work with trusted third-party vendors for analytics, payment processing, marketing, and fraud prevention. These vendors only access data necessary to perform their specific tasks and are <strong>strictly prohibited</strong> from using it for any other purpose.
                </li>
                <li>
                  <strong>Legal & Safety Requirements:</strong> We may disclose information if required by law (e.g., court orders) or if we determine in good faith that it is necessary to protect the rights, property, or safety of Famra Cure, our users, or others.
                </li>
                <li>
                  <strong>Core Operations:</strong> Traffic Data may be shared with partners who manage our hosting, security, and data storage.
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Note:</strong> We <strong>DO NOT</strong> sell your email addresses or phone numbers to third parties. We store and process data on secure servers in the United States and Europe using industry-standard backup system.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Public Reviews & Forums</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Public Visibility:</strong> Any comments or reviews you post are public and not private.</li>
                <li><strong>Think Before You Post:</strong> Avoid sharing sensitive personal or medical details in public discussions.</li>
                <li><strong>No Control:</strong> Once posted, your information can be viewed or used by third parties outside of our control.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">How We Secure Your Data</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Encryption:</strong> We use SSL technology to encrypt sensitive data during transmission and keep it in secure, private storage.</li>
                <li><strong>Security Limits:</strong> While we follow high industry standards, we cannot guarantee absolute security for any data sent over the internet.</li>
                <li><strong>Notifications:</strong> If we discover a security breach affecting your data, we will notify you as quickly as possible and provide details on the situation as required by law.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Managing Your Information</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-3 mb-4">
                <li>
                  <strong>Closing Your Account:</strong> If you wish to delete your account, please email us at <a href="mailto:solutions@famracure.com" className="font-bold text-sky-800 hover:text-sky-600 transition-colors">solutions@famracure.com</a>. We will process your request and delete your related information as soon as reasonably possible.
                </li>
                <li>
                  <strong>Data Retention:</strong> Please note that we reserve the right to retain certain information from closed accounts to comply with legal obligations, prevent fraud, resolve disputes, or enforce our agreements.
                </li>
                <li>
                  <strong>Security Alerts:</strong> You must notify us immediately if your account data is lost, stolen, or accessed without your permission.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Information Regarding Children</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Services are not intended for or directed toward children. Famra Cure does not knowingly collect information directly from anyone under the age of 13.
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-3 mb-4">
                <li>
                  <strong>Age Requirement:</strong> By using our Services, you represent that you are at least 13 years old. If we discover we have collected data from a child under 13 without verified parental consent, we will delete that information immediately after notifying the individual.
                </li>
                <li>
                  <strong>Minors (Ages 13–18):</strong> If you are between 13 and the age of majority in your jurisdiction, you may only use the Services under the supervision or with the consent of a parent or legal guardian.
                </li>
                <li>
                  <strong>Parental Use:</strong> Parents or guardians may use the Services on behalf of their minor children. Any data provided in this context will be protected as Personal Information under this policy.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Links to Other Sites</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We provide links to other websites for your convenience, but we do not control or endorse them.
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Privacy Rules:</strong> Once you leave Famra Cure, you are governed by the privacy policy of the new site.</li>
                <li><strong>User Caution:</strong> We are not responsible for how third parties handle your data. Always check a website's privacy settings before providing your information.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Policy Updates</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Notifications:</strong> We will post a notice on this page if we make major changes to how we handle your data.</li>
                <li><strong>Acceptance:</strong> Using Famra Cure after an update means you agree to the new terms.</li>
                <li><strong>Stay Informed:</strong> We recommend checking this page regularly.</li>
                <li><strong>Disagreement:</strong> If you don't agree with the new policy, please discontinue your use of our Services immediately.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">How We Use Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies to remember your preferences and provide a smoother experience.
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Control:</strong> You can block cookies in your browser settings if you prefer.</li>
                <li><strong>Trade-offs:</strong> If you turn off cookies, you may need to log in more frequently, and some site features might be limited. However, <strong>essential functions</strong> like booking a doctor's appointment will always remain available.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">COPPA Compliance</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We strictly follow COPPA guidelines to protect children's privacy online. Our services are not designed for or marketed to children under 13 years old, and we do not knowingly collect their personal data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">No Spam Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We value your privacy and do not send spam. We use your email address only for account verification and record-keeping of Users and Doctors signed up at Famra Cure. For your convenience, we primarily send registration confirmations and appointment details via email or SMS to ensure you get the timeliest updates.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Data Theft Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The data on our platform belongs solely to Famra Cure. It is not to be collected or used by third parties without our permission. We will take legal action against any person or group found stealing data from Famracure.com or our app.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Ownership of Content</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                All Rights Reserved. Every content, including videos, articles, and graphics on our website and app, belongs to Famra Cure. You may not copy, duplicate, or use our material without permission. Illegal use of our property for personal or business benefit is a violation of copyright law and will lead to legal consequences.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-sky-700">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                For any comment/question or concern; please feel free to contact us at: <a href="mailto:solutions@famracure.com" className="font-bold text-sky-800 hover:text-sky-600 transition-colors">solutions@famracure.com</a>
              </p>
            </section>

            <p className="text-sm text-gray-600 mt-8 pt-4 border-t">
              Last Updated: June 2026
            </p>
          </div>
        </div>
      </div>
      <FooterTopBar />
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
